import React, { useState, useEffect } from 'react';
import api from '../services/api';

export default function Users() {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm]         = useState({ email: '', nom: '', prenom: '', nom_commercial: '', npp: '', role: 'ADHERENT', region_ecf: '' });
  const [regions, setRegions]   = useState([]);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState(null);

  useEffect(() => {
    loadUsers();
    api.get('/regions/').then(r => setRegions(r.data)).catch(() => {});
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/users/');
      setUsers(data);
    } catch (e) {} finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.role === 'PRESIDENT_REGION' && !form.region_ecf) {
      setMsg({ type: 'error', text: 'La région ECF est obligatoire pour un Président de région' });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      await api.post('/users/', form);
      setMsg({ type: 'success', text: 'Utilisateur créé. Lien d\'activation envoyé.' });
      setForm({ email: '', nom: '', prenom: '', nom_commercial: '', npp: '', role: 'ADHERENT', region_ecf: '' });
      loadUsers();
      setTimeout(() => { setShowForm(false); setMsg(null); }, 2000);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally { setSaving(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      await api.put(`/users/${editUser.id}`, {
        nom: editUser.nom,
        prenom: editUser.prenom,
        nom_commercial: editUser.nom_commercial,
        npp: editUser.npp,
        role: editUser.role,
        region_ecf: editUser.region_ecf || null,
        statut: editUser.statut,
      });
      setMsg({ type: 'success', text: 'Utilisateur mis à jour.' });
      loadUsers();
      setTimeout(() => { setEditUser(null); setMsg(null); }, 1500);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Erreur' });
    } finally { setSaving(false); }
  };

  const handleToggleStatut = async (u) => {
    const newStatut = u.statut === 'ACTIF' ? 'INACTIF' : 'ACTIF';
    if (!window.confirm(`${newStatut === 'INACTIF' ? 'Désactiver' : 'Réactiver'} ${u.prenom} ${u.nom} ?`)) return;
    try {
      await api.put(`/users/${u.id}`, { statut: newStatut });
      loadUsers();
    } catch (e) {}
  };

  const handleResendActivation = async (u) => {
    try {
      await api.post(`/users/${u.id}/resend-activation`);
      alert('Lien d\'activation renvoyé.');
    } catch (e) {}
  };

  const ROLE_BADGE   = { ADHERENT: 'badge-grey', GESTIONNAIRE: 'badge-info', ADMIN: 'badge-error', PRESIDENT_REGION: 'badge-warning' };
  const STATUT_BADGE = { ACTIF: 'badge-success', INACTIF: 'badge-error', EN_ATTENTE: 'badge-warning' };

  const RoleSelect = ({ value, onChange }) => (
    <select className="form-input" value={value} onChange={e => onChange(e.target.value)} style={{ margin: 0 }}>
      <option value="ADHERENT">ADHÉRENT</option>
      <option value="GESTIONNAIRE">GESTIONNAIRE</option>
      <option value="ADMIN">ADMIN</option>
      <option value="PRESIDENT_REGION">PRÉSIDENT DE RÉGION</option>
    </select>
  );

  const RegionSelect = ({ value, onChange }) => (
    <select className="form-input" value={value || ''} onChange={e => onChange(e.target.value)} style={{ margin: 0 }}>
      <option value="">— Aucune —</option>
      {regions.map(r => (
        <option key={r.id} value={r.libelle}>RÉGION {r.numero} — {r.libelle}</option>
      ))}
    </select>
  );

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Utilisateurs</div>
        <button className="btn btn-primary" onClick={() => { setShowForm(true); setMsg(null); }}>
          + Nouvel utilisateur
        </button>
      </div>
      <div className="page-content">

        {/* Modal création */}
        {showForm && (
          <div className="modal-overlay">
            <div className="modal-box">
              <div className="modal-header">
                <span className="modal-title">Nouvel utilisateur</span>
                <button onClick={() => { setShowForm(false); setMsg(null); }} className="modal-close">✕</button>
              </div>
              <div className="modal-body">
                {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
                <form onSubmit={handleCreate}>
                  {[
                    ['email',          'Email',                      'email'],
                    ['nom',            'Nom',                        'text'],
                    ['prenom',         'Prénom',                     'text'],
                    ['nom_commercial', 'Nom commercial (auto-école)', 'text'],
                    ['npp',            'NPP (référence ECF)',         'text'],
                  ].map(([key, label, type]) => (
                    <div className="form-group" key={key}>
                      <label className="form-label">{label}</label>
                      <input className="form-input" type={type} value={form[key]}
                        onChange={e => setForm({ ...form, [key]: e.target.value })} required />
                    </div>
                  ))}
                  <div className="form-group">
                    <label className="form-label">Rôle</label>
                    <RoleSelect value={form.role} onChange={v => setForm({ ...form, role: v, region_ecf: '' })} />
                  </div>
                  {form.role === 'PRESIDENT_REGION' && (
                    <div className="form-group">
                      <label className="form-label">Région ECF <span style={{ color: 'var(--ecf-error)' }}>*</span></label>
                      <RegionSelect value={form.region_ecf} onChange={v => setForm({ ...form, region_ecf: v })} />
                    </div>
                  )}
                </form>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
                  {saving ? 'Création...' : 'Créer et envoyer le lien'}
                </button>
                <button className="btn btn-outline" onClick={() => { setShowForm(false); setMsg(null); }}>Annuler</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal édition */}
        {editUser && (
          <div className="modal-overlay">
            <div className="modal-box">
              <div className="modal-header">
                <span className="modal-title">Modifier l'utilisateur</span>
                <button onClick={() => { setEditUser(null); setMsg(null); }} className="modal-close">✕</button>
              </div>
              <div className="modal-body">
                {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}
                <form onSubmit={handleUpdate}>
                  {[
                    ['nom',            'Nom'],
                    ['prenom',         'Prénom'],
                    ['nom_commercial', 'Nom commercial'],
                    ['npp',            'NPP'],
                  ].map(([key, label]) => (
                    <div className="form-group" key={key}>
                      <label className="form-label">{label}</label>
                      <input className="form-input" value={editUser[key] || ''}
                        onChange={e => setEditUser({ ...editUser, [key]: e.target.value })} required />
                    </div>
                  ))}
                  <div className="form-group">
                    <label className="form-label">Rôle</label>
                    <RoleSelect value={editUser.role}
                      onChange={v => setEditUser({ ...editUser, role: v, region_ecf: '' })} />
                  </div>
                  {editUser.role === 'PRESIDENT_REGION' && (
                    <div className="form-group">
                      <label className="form-label">Région ECF <span style={{ color: 'var(--ecf-error)' }}>*</span></label>
                      <RegionSelect value={editUser.region_ecf}
                        onChange={v => setEditUser({ ...editUser, region_ecf: v })} />
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Statut</label>
                    <select className="form-input" value={editUser.statut}
                      onChange={e => setEditUser({ ...editUser, statut: e.target.value })} style={{ margin: 0 }}>
                      <option value="ACTIF">ACTIF</option>
                      <option value="INACTIF">INACTIF</option>
                      <option value="EN_ATTENTE">EN ATTENTE</option>
                    </select>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleUpdate} disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                <button className="btn btn-outline" onClick={() => { setEditUser(null); setMsg(null); }}>Annuler</button>
              </div>
            </div>
          </div>
        )}

        {/* Tableau */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ecf-mid-grey)' }}>
            <div className="card-title" style={{ margin: 0 }}>Comptes utilisateurs ({users.length})</div>
          </div>
          {loading ? (
            <div style={{ padding: 24, color: '#999' }}>Chargement...</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>NPP</th>
                    <th>Rôle</th>
                    <th>Région</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td className="important">
                        {u.prenom} {u.nom}
                        <br />
                        <small style={{ color: '#999', fontWeight: 400 }}>{u.nom_commercial}</small>
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>{u.email}</td>
                      <td>
                        <code style={{ fontSize: '0.78rem', background: '#f4f4f4', padding: '2px 6px' }}>
                          {u.npp}
                        </code>
                      </td>
                      <td><span className={`badge ${ROLE_BADGE[u.role] || 'badge-grey'}`}>{u.role}</span></td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--ecf-grey)' }}>{u.region_ecf || '—'}</td>
                      <td>
                        <span className={`badge ${STATUT_BADGE[u.statut] || 'badge-grey'}`}>
                          {u.statut}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button className="btn btn-outline"
                            style={{ padding: '3px 8px', fontSize: '0.72rem' }}
                            onClick={() => { setEditUser({ ...u }); setMsg(null); }}>
                            Modifier
                          </button>
                          {u.statut === 'EN_ATTENTE' && (
                            <button className="btn btn-outline"
                              style={{ padding: '3px 8px', fontSize: '0.72rem' }}
                              onClick={() => handleResendActivation(u)}>
                              Renvoyer lien
                            </button>
                          )}
                          {u.statut !== 'EN_ATTENTE' && (
                            <button className="btn btn-outline"
                              style={{ padding: '3px 8px', fontSize: '0.72rem', color: u.statut === 'ACTIF' ? 'var(--ecf-warning)' : 'var(--ecf-success)' }}
                              onClick={() => handleToggleStatut(u)}>
                              {u.statut === 'ACTIF' ? 'Désactiver' : 'Réactiver'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
